import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Lock, Puzzle, Search, ShieldAlert } from "lucide-react";
import { useState } from "react";

import { EmptyState } from "@/components/EmptyState";
import { PageHeader } from "@/components/PageHeader";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Dialog } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Table, TBody, TD, TH, THead, TR } from "@/components/ui/table";
import { Tabs } from "@/components/ui/tabs";
import { useSession } from "@/hooks/useSession";
import { api, type CatalogItem, type PluginDetail } from "@/lib/api";
import { cn } from "@/lib/utils";

const SRC_BADGE: Record<string, { label: string; variant: "success" | "warning" | "locked" | "secondary" }> = {
  core: { label: "open source", variant: "success" },
  external: { label: "external", variant: "warning" },
  pro: { label: "PRO", variant: "locked" },
};

const STATE: Record<string, { label: string; tone: string }> = {
  ready: { label: "enabled", tone: "text-emerald-300" },
  installed: { label: "installed", tone: "text-emerald-300" },
  available: { label: "available", tone: "text-zinc-400" },
  installable: { label: "needs artifact", tone: "text-amber-300" },
  entitled_pro_missing: { label: "locked — PRO", tone: "text-amber-300" },
  needs_credentials: { label: "needs credentials", tone: "text-amber-300" },
  needs_external_service: { label: "needs service", tone: "text-amber-300" },
  unavailable: { label: "unavailable", tone: "text-zinc-500" },
};

const LIFECYCLE: Record<string, string> = {
  discovered: "discovered", installed: "installed", configured: "configured",
  enabled: "enabled", healthy: "healthy", pending_restart: "pending restart",
  failed: "failed",
};

/** Detail drawer — typed settings, capability reasons, artifact evidence. */
function PluginDrawer({ id, onClose }: { id: string; onClose: () => void }) {
  const { user } = useSession();
  const qc = useQueryClient();
  const isOwner = user?.role === "owner";
  const [sq, setSq] = useState("");
  const [reason, setReason] = useState("");
  const [pending, setPending] = useState<null | { action: "enable" | "disable"; impact: string }>(null);

  const { data } = useQuery({
    queryKey: ["plugin", id],
    queryFn: () => api.get<PluginDetail>(`/api/plugin-catalog/${id}`),
  });
  const act = useMutation({
    mutationFn: async ({ action }: { action: "enable" | "disable" }) => {
      const r = await api.post<{ confirm_required?: boolean; impact?: string; ok?: boolean }>(
        `/api/plugins/${id}/${action}`, { reason: reason || "operator action", confirm_impact: !!pending });
      if (r.confirm_required) setPending({ action, impact: r.impact ?? "" });
      else { setPending(null); qc.invalidateQueries(); }
      return r;
    },
  });
  const save = useMutation({
    mutationFn: (settings: Record<string, string>) =>
      api.post(`/api/plugins/${id}/configure`, { settings }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["plugin", id] }),
  });
  const [draft, setDraft] = useState<Record<string, string>>({});

  if (!data) return null;
  const settings = Object.entries(data.settings)
    .filter(([k, s]) => !sq || k.toLowerCase().includes(sq.toLowerCase())
      || s.label?.toLowerCase().includes(sq.toLowerCase()))
    .sort(([a], [b]) => a.localeCompare(b));
  const st = data.state;

  return (
    <Dialog open onClose={onClose} title={data.name} wide>
      <div className="space-y-4 text-sm">
        <p className="text-muted-foreground">{data.description}</p>
        <div className="flex flex-wrap gap-2">
          <Badge variant={SRC_BADGE[data.source]?.variant ?? "secondary"}>
            {SRC_BADGE[data.source]?.label ?? data.source}
          </Badge>
          <Badge variant="secondary">{data.version || "—"}</Badge>
          <Badge variant="outline">{data.license || "license n/a"}</Badge>
          {st && <Badge variant="outline">{LIFECYCLE[st.lifecycle] ?? st.lifecycle}</Badge>}
          {st?.pending_restart && <Badge variant="warning">pending restart</Badge>}
        </div>

        {/* capability requirements — shown before any change */}
        {(data.capabilities?.external_service || data.capabilities?.credentials
          || data.capabilities?.data_sharing || data.capabilities?.entitlement) && (
          <div className="rounded-md border border-amber-500/30 bg-amber-500/5 p-3 text-xs">
            <div className="mb-1 flex items-center gap-1.5 font-medium text-amber-300">
              <ShieldAlert className="h-3.5 w-3.5" /> Requirements &amp; data flow
            </div>
            <ul className="list-inside list-disc space-y-0.5 text-muted-foreground">
              {data.capabilities.entitlement === "pro" && <li>BunkerWeb PRO entitlement required</li>}
              {data.capabilities.external_service && <li>External service: {data.capabilities.external_service}</li>}
              {data.capabilities.credentials && <li>Credentials: {data.capabilities.credentials}</li>}
              {data.capabilities.data_sharing && <li>Sends data: {data.capabilities.data_sharing}</li>}
            </ul>
          </div>
        )}
        {st?.disabled_reason && (
          <div className="rounded-md border border-destructive/40 bg-destructive/10 p-2 text-xs text-destructive">
            Disabled — {st.disabled_reason}
          </div>
        )}
        {st?.last_error && <div className="text-xs text-destructive">Last error: {st.last_error}</div>}
        {data.provenance && (
          <div className="font-mono text-[10px] text-muted-foreground/70">{data.provenance}</div>
        )}

        {/* impact preview flow */}
        {pending && (
          <div className="rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-xs">
            <div className="font-medium text-amber-300">Impact of {pending.action}:</div>
            <div className="mt-1 text-muted-foreground">{pending.impact}</div>
            <Input value={reason} onChange={(e) => setReason(e.target.value)}
              placeholder="Recorded reason (required)…" className="mt-2 h-8" />
            <div className="mt-2 flex gap-2">
              <Button size="sm" disabled={reason.trim().length < 3 || act.isPending}
                onClick={() => act.mutate({ action: pending.action })}>
                Confirm {pending.action}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setPending(null)}>Cancel</Button>
            </div>
          </div>
        )}
        {isOwner && !pending && st && (
          <div className="flex gap-2">
            {!st.enabled
              ? <Button size="sm" onClick={() => act.mutate({ action: "enable" })}>Enable…</Button>
              : <Button size="sm" variant="destructive" onClick={() => act.mutate({ action: "disable" })}>Disable…</Button>}
          </div>
        )}
        {act.isError && <div className="text-xs text-destructive">{String(act.error)}</div>}

        {/* typed settings — searchable */}
        <div>
          <div className="mb-2 flex items-center justify-between">
            <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Settings ({settings.length})
            </h4>
            <div className="relative w-52">
              <Search className="absolute left-2 top-2 h-3.5 w-3.5 text-muted-foreground" />
              <Input value={sq} onChange={(e) => setSq(e.target.value)} placeholder="Filter…" className="h-7 pl-7 text-xs" />
            </div>
          </div>
          <div className="max-h-72 space-y-1.5 overflow-y-auto pr-1">
            {settings.map(([key, spec]) => (
              <div key={key} className="grid grid-cols-[1fr,180px] items-center gap-2 rounded border border-border/60 px-2 py-1.5">
                <div className="min-w-0">
                  <div className="truncate font-mono text-xs">{key}
                    {spec.secret && <Lock className="ml-1 inline h-3 w-3 text-amber-400" />}
                  </div>
                  <div className="truncate text-[10px] text-muted-foreground" title={spec.help}>
                    {spec.label || spec.help || key}
                  </div>
                </div>
                {spec.type === "check" ? (
                  <select
                    className="h-7 rounded border bg-background px-1 text-xs"
                    value={draft[key] ?? st?.settings?.[key] ?? spec.default ?? "no"}
                    onChange={(e) => setDraft((d) => ({ ...d, [key]: e.target.value }))}
                  >
                    <option value="yes">yes</option><option value="no">no</option>
                  </select>
                ) : (
                  <Input className="h-7 font-mono text-xs"
                    type={spec.secret ? "password" : "text"}
                    placeholder={spec.default}
                    value={draft[key] ?? (spec.secret ? "" : st?.settings?.[key] ?? "")}
                    onChange={(e) => setDraft((d) => ({ ...d, [key]: e.target.value }))} />
                )}
              </div>
            ))}
          </div>
          {isOwner && Object.keys(draft).length > 0 && (
            <Button size="sm" className="mt-2" disabled={save.isPending}
              onClick={() => { save.mutate(draft); setDraft({}); }}>
              Save {Object.keys(draft).length} setting(s)
            </Button>
          )}
        </div>
      </div>
    </Dialog>
  );
}

export default function Plugins() {
  const [tab, setTab] = useState("all");
  const [q, setQ] = useState("");
  const [detail, setDetail] = useState<string | null>(null);

  const { data } = useQuery({
    queryKey: ["plugin-catalog"],
    queryFn: () => api.get<{ items: CatalogItem[]; counts: Record<string, number> }>("/api/plugin-catalog"),
    refetchInterval: 30_000,
  });
  const all = data?.items ?? [];
  const counts = {
    all: all.length,
    core: all.filter((p) => p.source === "core").length,
    external: all.filter((p) => p.source === "external").length,
    pro: all.filter((p) => p.source === "pro").length,
  };
  const filtered = all
    .filter((p) => (tab === "all" ? true : p.source === tab))
    .filter((p) => !q || p.name.toLowerCase().includes(q.toLowerCase())
      || p.id.includes(q.toLowerCase()));

  return (
    <div className="space-y-5">
      <PageHeader
        title="Plugin catalog"
        description={`${counts.all} plugins from the pinned BunkerWeb 1.6.15 release — ${data?.counts?.external ?? 0} official external integrations included. PRO entries are honestly locked; external plugins need an approved artifact.`}
      />
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Tabs value={tab} onChange={setTab} tabs={[
          { value: "all", label: "All", count: counts.all },
          { value: "core", label: "Core", count: counts.core },
          { value: "external", label: "External", count: counts.external },
          { value: "pro", label: "PRO", count: counts.pro },
        ]} />
        <div className="relative w-64">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search plugins…" className="pl-8" />
        </div>
      </div>

      <Card>
        <Table>
          <THead>
            <TR>
              <TH>Plugin</TH><TH>Source</TH><TH>Capability</TH><TH>Ver</TH>
              <TH>Settings</TH><TH>Requirements</TH>
            </TR>
          </THead>
          <TBody>
            {filtered.map((p) => {
              const src = SRC_BADGE[p.source] ?? { label: p.source, variant: "secondary" as const };
              const st = STATE[p.capability_state] ?? { label: p.capability_state, tone: "text-muted-foreground" };
              return (
                <TR key={p.id} className="cursor-pointer" onClick={() => setDetail(p.id)}>
                  <TD>
                    <div className="flex items-center gap-1.5 font-medium">
                      {p.capability_state === "entitled_pro_missing" && <Lock className="h-3.5 w-3.5 text-amber-400" />}
                      {p.name}
                    </div>
                    <div className="font-mono text-[10px] text-muted-foreground/70">{p.id}</div>
                  </TD>
                  <TD><Badge variant={src.variant}>{src.label}</Badge></TD>
                  <TD className={cn("text-xs", st.tone)} title={p.capability_reason}>{st.label}</TD>
                  <TD className="font-mono text-xs">{p.version || "—"}</TD>
                  <TD className="text-xs text-muted-foreground">{p.settings_count}</TD>
                  <TD className="max-w-[260px] text-xs text-muted-foreground">
                    {p.capabilities?.external_service && <div>svc: {p.capabilities.external_service}</div>}
                    {p.capabilities?.credentials && <div>creds: {p.capabilities.credentials}</div>}
                    {p.capabilities?.data_sharing && <div className="text-amber-300/80">shares: {p.capabilities.data_sharing}</div>}
                    {!p.capabilities?.external_service && !p.capabilities?.credentials && !p.capabilities?.data_sharing
                      && <span className="text-muted-foreground/50">self-contained</span>}
                  </TD>
                </TR>
              );
            })}
          </TBody>
        </Table>
        {filtered.length === 0 && (
          <EmptyState icon={Puzzle} title="No plugins match"
            hint={q ? `Nothing matching "${q}".` : "Catalog syncs from the pinned release."} />
        )}
      </Card>

      {detail && <PluginDrawer id={detail} onClose={() => setDetail(null)} />}
    </div>
  );
}
